/**
 * Spanish Move-Out Cleaning Landing Page
 * Route: /es/limpieza-mudanza/:citySlug
 * Handles every city/region with localized content.
 */
import { useParams, Navigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext, REGIONS } from "@/lib/locations";
import { getLocalContent } from "@/lib/city-content";
import { Guarantee } from "@/components/Guarantee";
import { Button } from "@/components/ui/button";
import { PrecioEstimadoWidget } from "@/components/PrecioEstimadoWidget";
import { MoveOutCouponPopup } from "@/components/MoveOutCouponPopup";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";
import {
  CheckCircle2, Star, Clock, Zap, Shield, Home,
  ArrowRight, ChevronRight, Sparkles, BadgeCheck
} from "lucide-react";

const BOOKING_URL = "/book";

// ── City / Region data ────────────────────────────────────────────────────────
const CITY_DATA: Record<string, {
  name: string;
  regionSlug: keyof typeof REGIONS;
  enPath: string;
  testimonialRegion: string;
  nearbyCities: string[];
}> = {
  // Sacramento Area
  sacramento:        { name: "Sacramento",        regionSlug: "sacramento-area", enPath: "/locations/sacramento-area", testimonialRegion: "sacramento",    nearbyCities: ["Roseville", "Folsom", "Elk Grove", "Rancho Cordova"] },
  roseville:         { name: "Roseville",         regionSlug: "sacramento-area", enPath: "/locations/roseville",        testimonialRegion: "roseville",     nearbyCities: ["Sacramento", "Rocklin", "Granite Bay", "Folsom"] },
  folsom:            { name: "Folsom",            regionSlug: "sacramento-area", enPath: "/locations/folsom",           testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "Roseville", "El Dorado Hills"] },
  "elk-grove":       { name: "Elk Grove",         regionSlug: "sacramento-area", enPath: "/locations/elk-grove",        testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "Rancho Cordova", "Citrus Heights"] },
  "rancho-cordova":  { name: "Rancho Cordova",    regionSlug: "sacramento-area", enPath: "/locations/rancho-cordova",   testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "Folsom", "Elk Grove"] },
  "granite-bay":     { name: "Granite Bay",       regionSlug: "sacramento-area", enPath: "/locations/granite-bay",      testimonialRegion: "roseville",     nearbyCities: ["Roseville", "Rocklin", "Sacramento"] },
  "el-dorado-hills": { name: "El Dorado Hills",   regionSlug: "sacramento-area", enPath: "/locations/el-dorado-hills",  testimonialRegion: "sacramento",    nearbyCities: ["Folsom", "Sacramento", "Placerville"] },
  "west-sacramento": { name: "West Sacramento",   regionSlug: "sacramento-area", enPath: "/locations/west-sacramento",  testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "Davis", "Woodland"] },
  carmichael:        { name: "Carmichael",        regionSlug: "sacramento-area", enPath: "/locations/carmichael",       testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "Citrus Heights", "Rancho Cordova"] },
  "citrus-heights":  { name: "Citrus Heights",    regionSlug: "sacramento-area", enPath: "/locations/citrus-heights",   testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "Carmichael", "Roseville"] },
  davis:             { name: "Davis",             regionSlug: "sacramento-area", enPath: "/locations/davis",            testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "West Sacramento", "Woodland"] },
  rocklin:           { name: "Rocklin",           regionSlug: "sacramento-area", enPath: "/locations/rocklin",          testimonialRegion: "roseville",     nearbyCities: ["Roseville", "Granite Bay", "Sacramento"] },

  // Los Angeles
  "los-angeles":       { name: "Los Ángeles",     regionSlug: "los-angeles", enPath: "/locations/los-angeles",               testimonialRegion: "la",        nearbyCities: ["Santa Mónica", "Pasadena", "Manhattan Beach", "Culver City"] },
  pasadena:            { name: "Pasadena",         regionSlug: "los-angeles", enPath: "/locations/la/la-san-gabriel-valley",  testimonialRegion: "pasadena",  nearbyCities: ["Arcadia", "San Marino", "Monrovia", "LA"] },
  "santa-monica":      { name: "Santa Mónica",     regionSlug: "los-angeles", enPath: "/locations/la/la-westside",           testimonialRegion: "west-la",   nearbyCities: ["Venice", "Culver City", "Brentwood", "West LA"] },
  venice:              { name: "Venice",           regionSlug: "los-angeles", enPath: "/locations/la/la-westside",            testimonialRegion: "west-la",   nearbyCities: ["Santa Mónica", "Culver City", "Brentwood"] },
  "culver-city":       { name: "Culver City",      regionSlug: "los-angeles", enPath: "/locations/la/la-westside",            testimonialRegion: "west-la",   nearbyCities: ["Santa Mónica", "Venice", "West LA"] },
  "sherman-oaks":      { name: "Sherman Oaks",     regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley", testimonialRegion: "north-la",  nearbyCities: ["Encino", "Studio City", "Woodland Hills"] },
  encino:              { name: "Encino",           regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley", testimonialRegion: "north-la",  nearbyCities: ["Sherman Oaks", "Woodland Hills", "Tarzana"] },
  "studio-city":       { name: "Studio City",      regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley", testimonialRegion: "north-la",  nearbyCities: ["Sherman Oaks", "Encino", "Burbank"] },
  "woodland-hills":    { name: "Woodland Hills",   regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley", testimonialRegion: "north-la",  nearbyCities: ["Encino", "Sherman Oaks", "Porter Ranch"] },
  "porter-ranch":      { name: "Porter Ranch",     regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley", testimonialRegion: "north-la",  nearbyCities: ["Woodland Hills", "Northridge", "Granada Hills"] },
  "manhattan-beach":   { name: "Manhattan Beach",  regionSlug: "los-angeles", enPath: "/locations/la/la-south-bay",           testimonialRegion: "south-la",  nearbyCities: ["Redondo Beach", "Torrance", "El Segundo"] },
  "redondo-beach":     { name: "Redondo Beach",    regionSlug: "los-angeles", enPath: "/locations/la/la-south-bay",           testimonialRegion: "south-la",  nearbyCities: ["Manhattan Beach", "Torrance", "Hermosa Beach"] },
  torrance:            { name: "Torrance",         regionSlug: "los-angeles", enPath: "/locations/la/la-south-bay",           testimonialRegion: "south-la",  nearbyCities: ["Redondo Beach", "Manhattan Beach", "Carson"] },
  "el-segundo":        { name: "El Segundo",       regionSlug: "los-angeles", enPath: "/locations/la/la-south-bay",           testimonialRegion: "south-la",  nearbyCities: ["Manhattan Beach", "Torrance", "Inglewood"] },
  arcadia:             { name: "Arcadia",          regionSlug: "los-angeles", enPath: "/locations/la/la-san-gabriel-valley",  testimonialRegion: "pasadena",  nearbyCities: ["Pasadena", "Monrovia", "San Marino"] },
  "san-marino":        { name: "San Marino",       regionSlug: "los-angeles", enPath: "/locations/la/la-san-gabriel-valley",  testimonialRegion: "pasadena",  nearbyCities: ["Pasadena", "Arcadia", "San Gabriel"] },
  monrovia:            { name: "Monrovia",         regionSlug: "los-angeles", enPath: "/locations/la/la-san-gabriel-valley",  testimonialRegion: "pasadena",  nearbyCities: ["Arcadia", "Pasadena", "Azusa"] },
  "long-beach":        { name: "Long Beach",       regionSlug: "los-angeles", enPath: "/locations/los-angeles",              testimonialRegion: "long-beach", nearbyCities: ["Torrance", "Lakewood", "Signal Hill"] },

  // San Francisco
  "san-francisco":     { name: "San Francisco",    regionSlug: "san-francisco", enPath: "/locations/san-francisco",      testimonialRegion: "san-francisco", nearbyCities: ["Daly City", "San Mateo", "Burlingame", "South SF"] },
  "daly-city":         { name: "Daly City",        regionSlug: "san-francisco", enPath: "/locations/daly-city",          testimonialRegion: "san-francisco", nearbyCities: ["San Francisco", "South SF", "Colma"] },
  "san-mateo":         { name: "San Mateo",        regionSlug: "san-francisco", enPath: "/locations/san-mateo",          testimonialRegion: "san-francisco", nearbyCities: ["Burlingame", "Foster City", "Belmont"] },
  burlingame:          { name: "Burlingame",       regionSlug: "san-francisco", enPath: "/locations/burlingame",         testimonialRegion: "san-francisco", nearbyCities: ["San Mateo", "Millbrae", "South SF"] },

  // Santa Clara / Silicon Valley
  "san-jose":          { name: "San José",         regionSlug: "santa-clara", enPath: "/locations/san-jose",          testimonialRegion: "san-jose",       nearbyCities: ["Santa Clara", "Sunnyvale", "Milpitas", "Los Gatos"] },
  "santa-clara":       { name: "Santa Clara",      regionSlug: "santa-clara", enPath: "/locations/santa-clara",       testimonialRegion: "santa-clara",     nearbyCities: ["San José", "Sunnyvale", "Cupertino"] },
  sunnyvale:           { name: "Sunnyvale",        regionSlug: "santa-clara", enPath: "/locations/sunnyvale",         testimonialRegion: "santa-clara",     nearbyCities: ["Mountain View", "Santa Clara", "Cupertino"] },
  "mountain-view":     { name: "Mountain View",    regionSlug: "santa-clara", enPath: "/locations/mountain-view",     testimonialRegion: "santa-clara",     nearbyCities: ["Palo Alto", "Sunnyvale", "Los Altos"] },
  "palo-alto":         { name: "Palo Alto",        regionSlug: "santa-clara", enPath: "/locations/palo-alto",         testimonialRegion: "silicon-valley",  nearbyCities: ["Mountain View", "Menlo Park", "Los Altos"] },
  cupertino:           { name: "Cupertino",        regionSlug: "santa-clara", enPath: "/locations/cupertino",         testimonialRegion: "santa-clara",     nearbyCities: ["Sunnyvale", "Saratoga", "Los Gatos"] },

  // Contra Costa
  "walnut-creek":      { name: "Walnut Creek",     regionSlug: "contra-costa", enPath: "/locations/walnut-creek",    testimonialRegion: "contra-costa",   nearbyCities: ["Concord", "Pleasant Hill", "Lafayette", "Danville"] },
  concord:             { name: "Concord",          regionSlug: "contra-costa", enPath: "/locations/concord",         testimonialRegion: "contra-costa",   nearbyCities: ["Walnut Creek", "Pleasant Hill", "Antioch"] },
  "san-ramon":         { name: "San Ramon",        regionSlug: "contra-costa", enPath: "/locations/san-ramon",       testimonialRegion: "tri-valley",     nearbyCities: ["Danville", "Dublin", "Pleasanton"] },
  danville:            { name: "Danville",         regionSlug: "contra-costa", enPath: "/locations/danville",        testimonialRegion: "contra-costa",   nearbyCities: ["San Ramon", "Walnut Creek", "Blackhawk"] },
  lafayette:           { name: "Lafayette",        regionSlug: "contra-costa", enPath: "/locations/lafayette",       testimonialRegion: "contra-costa",   nearbyCities: ["Walnut Creek", "Orinda", "Moraga"] },

  // Alameda / East Bay
  oakland:             { name: "Oakland",          regionSlug: "alameda", enPath: "/locations/oakland",              testimonialRegion: "oakland",        nearbyCities: ["Berkeley", "Alameda", "Emeryville", "San Leandro"] },
  berkeley:            { name: "Berkeley",         regionSlug: "alameda", enPath: "/locations/berkeley",             testimonialRegion: "east-bay",       nearbyCities: ["Oakland", "Albany", "El Cerrito", "Emeryville"] },
  fremont:             { name: "Fremont",          regionSlug: "alameda", enPath: "/locations/fremont",              testimonialRegion: "east-bay",       nearbyCities: ["Union City", "Newark", "Hayward"] },
  hayward:             { name: "Hayward",          regionSlug: "alameda", enPath: "/locations/hayward",              testimonialRegion: "east-bay",       nearbyCities: ["Fremont", "San Leandro", "Union City"] },
  pleasanton:          { name: "Pleasanton",       regionSlug: "alameda", enPath: "/locations/pleasanton",           testimonialRegion: "tri-valley",     nearbyCities: ["Dublin", "Livermore", "San Ramon"] },
  dublin:              { name: "Dublin",           regionSlug: "alameda", enPath: "/locations/dublin",               testimonialRegion: "tri-valley",     nearbyCities: ["Pleasanton", "Livermore", "San Ramon"] },
  livermore:           { name: "Livermore",        regionSlug: "alameda", enPath: "/locations/livermore",            testimonialRegion: "tri-valley",     nearbyCities: ["Pleasanton", "Dublin", "Tracy"] },

  // Solano
  vallejo:             { name: "Vallejo",          regionSlug: "solano", enPath: "/locations/vallejo",               testimonialRegion: "solano",         nearbyCities: ["Benicia", "Fairfield", "Hercules"] },
  fairfield:           { name: "Fairfield",        regionSlug: "solano", enPath: "/locations/fairfield",             testimonialRegion: "solano",         nearbyCities: ["Vacaville", "Vallejo", "Suisun City"] },
  vacaville:           { name: "Vacaville",        regionSlug: "solano", enPath: "/locations/vacaville",             testimonialRegion: "solano",         nearbyCities: ["Fairfield", "Dixon", "Davis"] },
  benicia:             { name: "Benicia",          regionSlug: "solano", enPath: "/locations/benicia",               testimonialRegion: "solano",         nearbyCities: ["Vallejo", "Martinez", "Hercules"] },

  // Marin
  "san-rafael":        { name: "San Rafael",       regionSlug: "marin", enPath: "/locations/san-rafael",             testimonialRegion: "marin",          nearbyCities: ["Novato", "San Anselmo", "Fairfax"] },
  novato:              { name: "Novato",           regionSlug: "marin", enPath: "/locations/novato",                 testimonialRegion: "marin",          nearbyCities: ["San Rafael", "Petaluma", "Mill Valley"] },
  "mill-valley":       { name: "Mill Valley",      regionSlug: "marin", enPath: "/locations/mill-valley",            testimonialRegion: "marin",          nearbyCities: ["Tiburón", "Sausalito", "Corte Madera"] },
  sausalito:           { name: "Sausalito",        regionSlug: "marin", enPath: "/locations/sausalito",             testimonialRegion: "marin",          nearbyCities: ["Mill Valley", "Corte Madera", "Tiburón"] },
  tiburon:             { name: "Tiburón",          regionSlug: "marin", enPath: "/locations/tiburon",                testimonialRegion: "marin",          nearbyCities: ["Sausalito", "Mill Valley", "Belvedere"] },

  // San Diego
  "san-diego":         { name: "San Diego",        regionSlug: "san-diego", enPath: "/locations/san-diego",          testimonialRegion: "san-diego",      nearbyCities: ["Chula Vista", "Carlsbad", "Oceanside", "Encinitas"] },
  "chula-vista":       { name: "Chula Vista",      regionSlug: "san-diego", enPath: "/locations/chula-vista",        testimonialRegion: "san-diego",      nearbyCities: ["San Diego", "National City", "Bonita"] },
  carlsbad:            { name: "Carlsbad",         regionSlug: "san-diego", enPath: "/locations/carlsbad",           testimonialRegion: "san-diego",      nearbyCities: ["Oceanside", "San Marcos", "Encinitas"] },
  oceanside:           { name: "Oceanside",        regionSlug: "san-diego", enPath: "/locations/oceanside",          testimonialRegion: "san-diego",      nearbyCities: ["Carlsbad", "Vista", "Camp Pendleton"] },
  encinitas:           { name: "Encinitas",        regionSlug: "san-diego", enPath: "/locations/encinitas",          testimonialRegion: "san-diego",      nearbyCities: ["Carlsbad", "Solana Beach", "Leucadia"] },

  // Orange County
  irvine:              { name: "Irvine",           regionSlug: "orange-county", enPath: "/locations/irvine",             testimonialRegion: "irvine",     nearbyCities: ["Costa Mesa", "Newport Beach", "Anaheim", "Mission Viejo"] },
  anaheim:             { name: "Anaheim",          regionSlug: "orange-county", enPath: "/locations/anaheim",            testimonialRegion: "irvine",     nearbyCities: ["Fullerton", "Orange", "Garden Grove"] },
  "santa-ana":         { name: "Santa Ana",        regionSlug: "orange-county", enPath: "/locations/santa-ana",          testimonialRegion: "irvine",     nearbyCities: ["Irvine", "Costa Mesa", "Garden Grove"] },
  "huntington-beach":  { name: "Huntington Beach", regionSlug: "orange-county", enPath: "/locations/huntington-beach",   testimonialRegion: "irvine",     nearbyCities: ["Costa Mesa", "Newport Beach", "Fountain Valley"] },
  "newport-beach":     { name: "Newport Beach",    regionSlug: "orange-county", enPath: "/locations/newport-beach",      testimonialRegion: "irvine",     nearbyCities: ["Irvine", "Costa Mesa", "Laguna Beach"] },
  "costa-mesa":        { name: "Costa Mesa",       regionSlug: "orange-county", enPath: "/locations/costa-mesa",         testimonialRegion: "irvine",     nearbyCities: ["Newport Beach", "Irvine", "Huntington Beach"] },
  "mission-viejo":     { name: "Mission Viejo",    regionSlug: "orange-county", enPath: "/locations/mission-viejo",      testimonialRegion: "irvine",     nearbyCities: ["Laguna Hills", "Irvine", "Aliso Viejo"] },
};

const CHECKLIST = [
  "Interior de todos los gabinetes y cajones",
  "Interior del horno, refrigerador y microondas",
  "Limpieza profunda de baños — azulejos, lechada, ducha e inodoro",
  "Ventanas interiores y marcos",
  "Zócalos, molduras y marcos de puertas",
  "Pisos, alfombras y huellas en paredes",
  "Ventiladores de techo y luces",
  "Todos los closets y armarios vaciados",
];

const STEPS = [
  { icon: Zap, title: "Reserva en 60 segundos", desc: "Ingresa el tamaño de tu hogar y obtén tu precio exacto al instante. Sin visitas previas." },
  { icon: Clock, title: "Confirmamos tu cita", desc: "Recibirás confirmación inmediata por correo electrónico con todos los detalles." },
  { icon: Sparkles, title: "Limpiamos a fondo", desc: "Nuestro equipo verificado sigue nuestra lista de 75 puntos para dejarlo todo impecable." },
  { icon: BadgeCheck, title: "Garantía del 200%", desc: "Si algo no está perfecto, volvemos sin costo. Si sigues insatisfecho, te devolvemos el dinero." },
];

const SpanishMoveOutPage = () => {
  const { citySlug } = useParams<{ citySlug: string }>();

  // Allow both slug and a special "all" catch to use a default
  const citySlugNorm = (citySlug || "sacramento").toLowerCase().replace(/\s+/g, "-");
  const cityData = CITY_DATA[citySlugNorm];

  if (!cityData) {
    // Try looking up a normalised version
    const found = Object.entries(CITY_DATA).find(([k]) => k === citySlugNorm);
    if (!found) return <Navigate to="/es/sacramento" replace />;
  }

  const { name, regionSlug, enPath, testimonialRegion, nearbyCities } = cityData || CITY_DATA["sacramento"];

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: checklistRef, isVisible: checklistVisible } = useScrollAnimation();
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <LocationContext.Provider value={{ slug: citySlugNorm, name, type: "city", regionSlug }}>
      <SEO
        title={`Limpieza de Mudanza en ${name} — Recupera Tu Depósito | Uplevel Cleaning`}
        description={`Servicio profesional de limpieza de mudanza en ${name}. Precio exacto en segundos, lista de 75 puntos y Garantía de Satisfacción del 200%. ¡Reserva hoy!`}
        keywords={`limpieza de mudanza ${name}, limpieza al salir ${name}, limpieza mudanza ${name.toLowerCase()}, servicio de limpieza mudanza ${name.toLowerCase()}, depósito de seguridad ${name.toLowerCase()}, uplevel cleaning`}
        url={`https://uplevelcleaning.com/es/limpieza-mudanza/${citySlugNorm}`}
        alternateUrl={`https://uplevelcleaning.com${enPath}`}
        alternateLang="en-US"
        locationName={name}
        regionSlug={regionSlug}
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        includeService={true}
        serviceName="Limpieza de Mudanza"
        serviceDescription={`Limpieza profesional de mudanza en ${name}. Lista de 75 puntos, precio plano y Garantía del 200%.`}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: name, url: `/es/${citySlugNorm}` },
          { name: "Limpieza de Mudanza", url: `/es/limpieza-mudanza/${citySlugNorm}` }
        ]}
        reviews={[
          { author: "Cliente Local", rating: 5, reviewBody: `Recuperé mi depósito completo gracias a la limpieza de mudanza de Uplevel en ${name}. ¡Servicio impecable!`, datePublished: "2026-04-01" },
          { author: "Inquilino Satisfecho", rating: 5, reviewBody: `El mejor servicio de limpieza de mudanza en ${name}. Puntuales, detallistas y precio exacto.`, datePublished: "2026-03-20" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <MoveOutCouponPopup cityName={name} delay={20000} />
        <main className="flex-1">

          {/* ── HERO ─────────────────────────────────────────────────────── */}
          <section className="relative py-20 lg:py-32 bg-background overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background pointer-events-none" />
            <div className="container relative z-10">
              <div
                ref={heroRef}
                className={cn("max-w-4xl mx-auto text-center anim-fade-up", heroVisible && "is-visible")}
              >
                {/* Breadcrumb */}
                <nav className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-6">
                  <Link to="/" className="hover:text-primary transition-colors">Inicio</Link>
                  <ChevronRight className="h-3 w-3" />
                  <Link to={`/es/${citySlugNorm}`} className="hover:text-primary transition-colors">{name}</Link>
                  <ChevronRight className="h-3 w-3" />
                  <span className="text-foreground font-medium">Limpieza de Mudanza</span>
                </nav>

                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Home className="h-4 w-4" />
                  Limpieza de Mudanza Profesional en {name}
                </div>

                <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-6 leading-[1.1]">
                  Limpieza de Mudanza en{" "}
                  <span className="text-primary">{name}</span>
                  <br className="hidden md:block" />
                  Recupera Tu Depósito Completo
                </h1>

                <p className="text-xl text-muted-foreground mb-4 max-w-2xl mx-auto">
                  Nuestro equipo verificado limpia cada rincón de tu hogar siguiendo una lista de 75 puntos. Propietarios exigentes, inspecciones rigurosas — nosotros lo manejamos todo.
                </p>
                
                <p className="text-base text-muted-foreground mb-4 max-w-2xl mx-auto border-l-4 border-primary pl-4 text-left">
                  {getLocalContent(name, regionSlug, 'es').localIssuesText}
                </p>

                <p className="text-lg font-semibold text-primary mb-10">
                  Precio exacto en segundos. Sin sorpresas. Garantía del 200%.
                </p>

                {/* Dual CTA */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                  <Button asChild size="lg" className="h-14 px-10 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 btn-pulse">
                    <a href={BOOKING_URL}>
                      <Zap className="h-5 w-5 mr-2" />
                      Reservar Ahora
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-14 px-10 text-lg font-bold border-2 border-primary text-primary hover:bg-primary/10">
                    <a href={BOOKING_URL}>
                      <Sparkles className="h-5 w-5 mr-2" />
                      Obtener Precio al Instante
                    </a>
                  </Button>
                </div>

                {/* Trust badges */}
                <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /><span>Asegurados y verificados</span></div>
                  <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-primary" /><span>Garantía del 200%</span></div>
                </div>
              </div>
            </div>
          </section>

          {/* ── INLINE CTA STRIP ────────────────────────────────────────── */}
          <section className="py-8 bg-primary text-primary-foreground">
            <div className="container flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-lg font-bold">¿Listo para recuperar tu depósito en {name}?</p>
                <p className="text-sm opacity-80">Precio plano, sin visita previa, sin sorpresas. Reserva en 60 segundos.</p>
              </div>
              <div className="flex gap-3 shrink-0">
                <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 hover:text-primary font-bold">
                  <a href={BOOKING_URL}><Zap className="h-4 w-4 mr-2" />Reservar</a>
                </Button>
                <Button asChild size="lg" className="bg-white/10 border border-white/30 text-white hover:bg-white/20 hover:text-white font-bold">
                  <a href={BOOKING_URL}><Sparkles className="h-4 w-4 mr-2" />Precio Exacto</a>
                </Button>
              </div>
            </div>
          </section>

          {/* ── CHECKLIST ────────────────────────────────────────────────── */}
          <section className="py-20 bg-muted/30">
            <div className="container">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div
                  ref={checklistRef}
                  className={cn("anim-slide-left", checklistVisible && "is-visible")}
                >
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    ¿Qué Incluye Nuestra Limpieza de Mudanza en {name}?
                  </h2>
                  <p className="text-muted-foreground mb-8">
                    Nuestra lista de 75 puntos cubre cada centímetro de tu propiedad para que pases la inspección del propietario con total confianza.
                  </p>
                  <ul className="space-y-4">
                    {CHECKLIST.map((item, i) => (
                      <li key={i} className={cn("flex items-start gap-3 anim-fade-up", checklistVisible && "is-visible")} style={{ transitionDelay: `${i * 60}ms` }}>
                        <div className="mt-0.5 h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                    <p className="text-sm font-semibold text-primary flex items-center gap-2">
                      <BadgeCheck className="h-4 w-4" />
                      Garantía del 200% — Si algo no está perfecto, volvemos gratis. Si sigues insatisfecho, te devolvemos el dinero.
                    </p>
                  </div>
                </div>

                <div className={cn("relative anim-slide-right", checklistVisible && "is-visible")}>
                  <div className="rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src="https://vibe.filesafe.space/1776798830316075366/assets/d9009cf2-193c-419e-ab3f-14ed63ffbc07.png"
                      alt={`Limpieza de mudanza profesional en ${name}`}
                      className="w-full h-full object-cover aspect-[4/3]"
                      loading="lazy"
                    />
                  </div>
                  {/* Floating badge */}
                  <div className="absolute -bottom-6 -left-6 bg-card p-5 rounded-2xl shadow-xl border hidden md:flex items-center gap-4">
                    <div className="bg-green-500/10 p-3 rounded-xl">
                      <BadgeCheck className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-bold text-sm">98% recuperan su depósito</p>
                      <p className="text-xs text-muted-foreground">con nuestra limpieza de mudanza</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
          <section className="py-20 bg-background">
            <div className="container">
              <div className={cn("text-center mb-14 anim-fade-up", stepsVisible && "is-visible")} ref={stepsRef}>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Cómo Funciona en {name}</h2>
                <p className="text-muted-foreground max-w-xl mx-auto">Reserva tu limpieza de mudanza en 60 segundos. Sin estrés, sin sorpresas.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {STEPS.map((step, i) => (
                  <div
                    key={i}
                    className={cn("text-center p-6 rounded-2xl border bg-card hover:shadow-lg hover:-translate-y-1 transition-all duration-300 anim-fade-up", stepsVisible && "is-visible")}
                    style={{ transitionDelay: `${i * 100}ms` }}
                  >
                    <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <step.icon className="h-7 w-7 text-primary" />
                    </div>
                    <div className="text-xs font-bold text-primary mb-2 uppercase tracking-widest">Paso {i + 1}</div>
                    <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                    <p className="text-muted-foreground text-sm">{step.desc}</p>
                  </div>
                ))}
              </div>

              {/* Mid CTA */}
              <div className="mt-12 text-center">
                <Button asChild size="lg" className="h-14 px-10 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 btn-pulse">
                  <a href={BOOKING_URL}>
                    <Zap className="h-5 w-5 mr-2" />
                    Obtener Mi Precio Ahora
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* ── PRICING HIGHLIGHT + CALCULATOR ───────────────────────────── */}
          <section className="py-16 bg-muted/30">
            <div className="container">
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                <h2 className="text-3xl font-bold text-center mb-10">Precios de Limpieza de Mudanza en {name}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { size: "1 Recámara", price: "desde $249", note: "Apartamentos y estudios" },
                    { size: "2–3 Recámaras", price: "desde $318", note: "Casas y condominios medianos", highlight: true },
                    { size: "4+ Recámaras", price: "desde $450", note: "Casas grandes y mansiones" },
                  ].map((tier, i) => (
                    <div key={i} className={cn(
                      "rounded-2xl p-6 text-center border transition-all hover:-translate-y-1 hover:shadow-lg",
                      tier.highlight ? "bg-primary text-primary-foreground border-primary shadow-lg" : "bg-card border-border"
                    )}>
                      <p className={cn("text-sm font-semibold mb-1 uppercase tracking-wider", tier.highlight ? "text-primary-foreground/80" : "text-muted-foreground")}>{tier.size}</p>
                      <p className="text-4xl font-extrabold mb-1">{tier.price}</p>
                      <p className={cn("text-sm", tier.highlight ? "text-primary-foreground/80" : "text-muted-foreground")}>{tier.note}</p>
                      {tier.highlight && (
                        <span className="mt-3 inline-block bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">Más popular</span>
                      )}
                    </div>
                   ))}
                </div>
                <p className="text-center text-sm text-muted-foreground mt-6">
                  * Precios planos — sin tarifas por hora, sin sorpresas. Obtén tu precio exacto en línea en segundos.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button asChild size="lg" className="h-14 px-10 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90">
                    <a href={BOOKING_URL}><Sparkles className="h-5 w-5 mr-2" />Calcular Mi Precio</a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-14 px-10 text-lg font-bold border-2 border-primary text-primary hover:bg-primary/10">
                    <a href={BOOKING_URL}><Zap className="h-5 w-5 mr-2" />Reservar Ahora</a>
                  </Button>
                </div>
              </div>

              {/* ── Inline Calculator Widget ─────────────────────────── */}
              <div className="lg:sticky lg:top-28">
                <PrecioEstimadoWidget cityName={name} serviceType="mudanza" />
              </div>
            </div>
          </section>

          {/* ── TESTIMONIALS ─────────────────────────────────────────────── */}
          <SpanishTestimonials region={testimonialRegion as any} />

          {/* ── GUARANTEE ────────────────────────────────────────────────── */}
          <Guarantee />

          {/* ── FAQ ──────────────────────────────────────────────────────── */}
          <section className="py-20 bg-background">
            <div className="container max-w-3xl">
              <h2 className="text-3xl font-bold text-center mb-12">Preguntas Frecuentes sobre Limpieza de Mudanza en {name}</h2>
              <div className="space-y-4">
                {[
                  { q: `¿Cuánto cuesta la limpieza de mudanza en ${name}?`, a: "Los precios empiezan desde $249 para apartamentos de 1 recámara. Obtienes tu precio exacto en segundos en línea — sin visita previa requerida." },
                  { q: "¿Qué diferencia hay entre limpieza estándar y limpieza de mudanza?", a: "La limpieza de mudanza es mucho más exhaustiva. Incluye el interior de todos los gabinetes y electrodomésticos, zócalos, marcos de puertas, ventanas interiores y todas las superficies que normalmente no se cubren en la limpieza regular." },
                  { q: "¿Necesito estar presente durante la limpieza?", a: "No. La mayoría de nuestros clientes nos dejan instrucciones de acceso (código de caja de seguridad, llave con el portero, etc.) y nos vamos cuando terminamos." },
                  { q: "¿Qué pasa si el propietario no está satisfecho?", a: "Nuestra Garantía del 200% cubre exactamente eso: si algo no pasa la inspección, volvemos a limpiar sin costo adicional. Si aun así no estás satisfecho, te devolvemos el dinero completo." },
                  { q: `¿Con cuánta anticipación debo reservar en ${name}?`, a: "Recomendamos reservar con 2–5 días de anticipación, aunque solemos tener disponibilidad en la misma semana. Reserva en línea en menos de 60 segundos." },
                  { q: "¿Traen sus propios suministros?", a: "Sí. Nuestro equipo llega con todos los productos y equipos de limpieza necesarios. No tienes que preocuparte por nada." },
                ].map((faq, i) => (
                  <details key={i} className="group border border-border rounded-2xl overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold hover:bg-muted/30 transition-colors">
                      <span>{faq.q}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-open:rotate-90 transition-transform" />
                    </summary>
                    <div className="p-5 pt-0 text-muted-foreground">{faq.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* ── NEARBY CITIES ────────────────────────────────────────────── */}
          <section className="py-16 bg-muted/30">
            <div className="container">
              <h2 className="text-2xl font-bold text-center mb-8">También Servimos Ciudades Cercanas</h2>
              <div className="flex flex-wrap justify-center gap-3">
                {nearbyCities.map((city) => {
                  const slug = city.toLowerCase().replace(/\s+/g, "-").replace(/[áéíóú]/g, (c) => ({ á: "a", é: "e", í: "i", ó: "o", ú: "u" }[c] || c));
                  return (
                    <Link
                      key={city}
                      to={`/es/limpieza-mudanza/${slug}`}
                      className="px-4 py-2 rounded-full border border-primary/30 text-primary text-sm font-medium hover:bg-primary/10 hover:border-primary transition-colors"
                    >
                      {city}
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ── FINAL CTA ────────────────────────────────────────────────── */}
          <section
            ref={ctaRef}
            className={cn("py-24 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-center anim-scale-up", ctaVisible && "is-visible")}
          >
            <div className="container max-w-3xl">
              <h2 className="text-3xl md:text-5xl font-extrabold mb-4">
                ¿Lista tu mudanza en {name}?
              </h2>
              <p className="text-xl opacity-90 mb-10">
                Reserva tu limpieza de mudanza hoy. Precio exacto en segundos, sin compromiso, Garantía del 200%.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="h-16 px-12 text-xl font-bold bg-white text-primary hover:bg-white/90 hover:text-primary btn-pulse">
                  <a href={BOOKING_URL}><Zap className="h-5 w-5 mr-2" />Reservar Ahora</a>
                </Button>
                <Button asChild size="lg" className="h-16 px-12 text-xl font-bold bg-white/10 border-2 border-white/40 text-white hover:bg-white/20 hover:text-white">
                  <a href={BOOKING_URL}><Sparkles className="h-5 w-5 mr-2" />Obtener Precio Exacto</a>
                </Button>
              </div>
              <p className="mt-6 text-sm opacity-75">Sin cargos ocultos · Sin tarifas por hora · 100% Garantizado</p>
            </div>
          </section>

        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default SpanishMoveOutPage;
